import SyncAltIcon from '@mui/icons-material/SyncAlt';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from '@mui/material';
import { useIntl } from 'react-intl';

import { useFeatureFlagSplit } from '@verantos-dev/core-utils';
import { Button } from '@verantos-dev/shared-components';

import { AllocationSummary } from '../../types/response';

interface Props {
  allocationSummary: AllocationSummary[];
  /** Accepted for caller compatibility. This component renders its content immediately and does not use it. */
  isLoading?: boolean;
  /** Show the "Reallocate encounters" action (edit mode only). */
  editable?: boolean;
  onReallocate?: () => void;
}

interface Batch {
  name: string;
  key: 'validationCount' | 'trainingCount' | 'testCount';
}
export const AllocationSummaryTable = ({
  allocationSummary,
  editable = false,
  onReallocate,
}: Props) => {
  const batches: Batch[] = [
    {
      name: 'Validation',
      key: 'validationCount',
    },
    {
      name: 'Training',
      key: 'trainingCount',
    },
    {
      name: 'Test',
      key: 'testCount',
    },
  ] as const;
  const theme = useTheme();
  const { formatMessage } = useIntl();
  // TODO: cleanup with at_allocation_summ_table_alignment_fix ff
  const isAllocationSummaryAlignmentFixEnabled = useFeatureFlagSplit(
    'at_allocation_summ_table_alignment_fix'
  ) as boolean;
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(3) }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: theme.spacing(2),
        }}
      >
        <Typography variant="h6">
          {formatMessage({ id: 'adminProjects.overview.allocationSummary.title' })}
        </Typography>
        {editable && (
          <Button
            id="reallocate-encounters-button"
            variant="outlined"
            startIcon={<SyncAltIcon />}
            onClickHandler={onReallocate}
            displayLabel={formatMessage({ id: 'adminProjects.reallocation.button' })}
            sx={{
              px: theme.spacing(3),
              textTransform: 'none',
              fontSize: '14px',
              color: theme.palette.primary.main,
            }}
          />
        )}
      </Box>
      <Paper variant="outlined">
        <Table>
          <TableHead>
            <TableRow
              sx={{
                '&:hover': { backgroundColor: 'transparent' },
                borderBottom: `2px solid ${theme.palette.divider}`,
                backgroundColor: theme.palette.grey[100],
              }}
            >
              <TableCell>Batch</TableCell>
              <TableCell>Annotator</TableCell>
              <TableCell align="right">Encounters allocated</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {batches.map((batch) =>
              allocationSummary.map((row, index) => (
                <TableRow
                  key={`${batch.name}-${row.annotatorId}`}
                  sx={{
                    '&:hover': { backgroundColor: 'transparent' },
                    borderBottom: `1px solid ${theme.palette.divider}`,
                  }}
                >
                  {index === 0 && (
                    <TableCell
                      rowSpan={allocationSummary.length}
                      sx={{ verticalAlign: 'top', fontWeight: 500 }}
                    >
                      {batch.name}
                    </TableCell>
                  )}

                  <TableCell
                    sx={
                      isAllocationSummaryAlignmentFixEnabled
                        ? {
                            maxWidth: 250,
                            overflowWrap: 'anywhere',
                          }
                        : undefined
                    }
                  >
                    {row.annotatorName}
                  </TableCell>

                  <TableCell align="right">{row[batch.key].toLocaleString()}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Paper>
    </Box>
  );
};
